/**
 * English (US) locale for phone-numbers page.
 * Export is the "phoneNumbers" subtree for host i18n merge.
 */
export const phoneNumbersEnUs = {
  list: {
    title: `Phone Numbers`,
    serverGroup: `Server Group:`,
    searchPlaceholder: `Search...`,
    loading: `Loading phone numbers...`,
    error: `Failed to load phone numbers`,
    empty: `No phone numbers found for this PBX`,
    noResults: `No phone numbers match your search`,
    columnName: `Name`,
    columnNumber: `Number`,
    columnExtension: `Extension`,
    columnCallerId: `Caller ID`,
    columnType: `Type`,
    columnTollFree: `Toll Free`,
    columnSms: `SMS`,
    columnE911: `e911`,
    columnVoiceProvider: `Voice Provider`,
    columnSmsProvider: `SMS Provider`,
    columnProviders: `Providers`,
    columnStatus: `Status`,
    columnUpdated: `Updated`,
    yes: `Yes`,
    no: `No`,
    smsActive: `Active`,
    e911Verified: `Verified`,
    createButton: `+ Create Phone Number`,
    createDisabledTooltip: `Phone number creation is managed by the phone team`,
    filterButton: `Filters`,
    copyNumber: `Copy Number`,
    toast: {
      createSuccess: `Phone number created`,
      createFailed: `Failed to create phone number`,
      copied: `Number copied to clipboard`,
      copyFailed: `Failed to copy number`,
    },
  },
  filters: {
    status: `Status`,
    allStatuses: `All`,
    type: `Type`,
    allTypes: `All`,
    voiceProvider: `Voice Provider`,
    smsProvider: `SMS Provider`,
    allProviders: `All`,
    tollFree: `Toll Free`,
    any: `Any`,
    showDeleted: `Show Deleted`,
    clearAll: `Clear Filters`,
  },
  types: {
    callerId: `Caller ID`,
    smsOnly: `SMS Only`,
    standard: `Standard`,
    temporary: `Temporary`,
  },
  bulk: {
    title: `Bulk Update Status`,
    description: `Update status for the selected phone number(s).`,
    selected: `{{count}} selected`,
    apply: `Apply`,
    updateStatus: `Update Status`,
    clearSelection: `Clear`,
    maxExceeded: `Max 25 at a time`,
    selectAll: `Select all phone numbers`,
    selectRow: `Select {{number}}`,
    toast: {
      success: `Status updated successfully`,
      failed: `Failed to bulk update status`,
    },
    results: {
      titleSuccess: `Bulk Update Completed`,
      titleAllFailed: `Bulk Update Failed`,
      titlePartial: `Bulk Update Partially Completed`,
      summarySuccess: `Successfully updated {{count}} phone number(s).`,
      summaryAllFailed: `All {{count}} phone number(s) failed to update.`,
      summaryPartial: `{{successCount}} of {{totalCount}} updated. {{failedCount}} failed:`,
      colNumber: `Number`,
      colStatus: `Current Status`,
      colError: `Error`,
      close: `Close`,
    },
  },
  detail: {
    loading: `Loading phone number...`,
    error: `Failed to load phone number`,
    backToList: `Back to list`,
    editButton: `Edit Phone Number`,
    deleteTitle: `Delete Phone Number`,
    deleteButton: `Delete`,
    deleteConfirmation: `This will soft-delete the phone number. It can be restored later if needed. Are you sure?`,
    restoreButton: `Restore`,
    noopSaveButton: `Re-save`,
    deleted: `Deleted`,
    sectionGeneral: `General Information`,
    sectionProviders: `Providers`,
    sectionRouting: `Routing`,
    sectionSms: `SMS`,
    sectionE911: `E911 Information`,
    sectionTimestamps: `Timestamps`,
    sectionSmsDiagnostics: `Telnyx SMS Diagnostics`,
    diagnosticWarning: `Warning`,
    fieldName: `Name`,
    fieldNumber: `Number`,
    fieldPbx: `PBX`,
    fieldType: `Type`,
    fieldCallerId: `Caller ID`,
    fieldExtension: `Extension`,
    fieldTollFree: `Toll Free`,
    fieldCountryCode: `Country Code`,
    fieldLocalPresence: `Local Presence`,
    fieldCampaignId: `Campaign ID`,
    fieldVoiceProvider: `Voice Provider`,
    fieldSmsProvider: `SMS Provider`,
    fieldSmsEnabled: `SMS Enabled`,
    fieldSmsStatus: `SMS Status`,
    fieldSmsRelay: `SMS Relay`,
    fieldSmsRelayIntegration: `SMS Relay Integration`,
    fieldSmsGroup: `SMS Group`,
    fieldE911Status: `E911 Status`,
    fieldE911Address: `Address`,
    fieldE911Address2: `Address 2`,
    fieldE911City: `City`,
    fieldE911State: `State`,
    fieldSendFaxesFrom: `Send Faxes From`,
    usePbxDefault: `Use PBX Default`,
    fieldPbxServerGroup: `PBX Server Group`,
    fieldSourceTracking: `Source Tracking`,
    fieldStatus: `Status`,
    fieldE911PostalCode: `Postal Code`,
    fieldCreatedAt: `Created`,
    fieldUpdatedAt: `Updated`,
    viewInProvider: `View in {{provider}}`,
    sectionMetrics: `Metrics`,
    fieldCallStats: `Call Stats`,
    callStatsLink: `Call Stats`,
    on: `On`,
    off: `Off`,
    inactive: `Inactive`,
    registered: `Registered`,
    notRegistered: `Not Registered`,
    toast: {
      updateSuccess: `Phone number updated`,
      updateFailed: `Failed to update phone number`,
      deleteSuccess: `Phone number deleted`,
      deleteFailed: `Failed to delete phone number`,
      restoreSuccess: `Phone number restored`,
      restoreFailed: `Failed to restore phone number`,
      noopSaveSuccess: `Re-triggered side effects`,
      noopSaveFailed: `Re-save failed`,
    },
  },
  edit: {
    title: `Edit Phone Number`,
    numberLabel: `Number`,
    nameLabel: `Name`,
    namePlaceholder: `Phone number name`,
    typeLabel: `Type`,
    statusLabel: `Status`,
    selectStatus: `— Select Status —`,
    voiceProviderLabel: `Voice Provider`,
    smsProviderLabel: `SMS Provider`,
    selectProvider: `— Select —`,
    unassigned: `Unassigned`,
    callerIdLabel: `Caller ID`,
    sendFaxesFromLabel: `Send Faxes From`,
    usePbxDefault: `Use PBX Default`,
    extensionLabel: `Extension`,
    extensionPlaceholder: `Extension UUID`,
    extensionHelp: `Optional extension for the phone number.`,
    noExtension: `None`,
    sourceTrackingLabel: `Source Tracking`,
    sourceTrackingHelp: `Prepend up to 6 characters to the Caller ID for inbound calls. This is commonly used to track ad campaigns.`,
    localPresenceLabel: `Local Presence`,
    localPresenceHelp: `Local presence groups create a pool of numbers that a local-presence-enabled line extension can place a call from in order to appear local to the callee.`,
    e911Section: `Emergency 911 (E911) Settings`,
    e911AddressLabel: `Address`,
    e911Address2Label: `Address apartment/suite`,
    e911CityLabel: `City`,
    e911StateLabel: `State/Province`,
    e911PostalCodeLabel: `Postal Code`,
    e911Disclaimer: `E911 is a system used in North America to automatically provide the caller's location to 911 dispatchers. Please make sure this information is accurate--someone's life could depend on it.`,
    smsSection: `SMS Settings`,
    smsEnabledLabel: `SMS Enabled`,
    smsEnabledWarning: `Warning: enabling SMS will trigger a port-in request of the eSPID on this number, which may cause SMS downtime for the customer. Please make sure this is the desired action before proceeding.`,
    smsRelayLabel: `SMS Relay`,
    smsRelayIntegrationLabel: `SMS Relay Integration`,
    smsRelayIntegrationDisabledHelp: `Enable SMS Relay to choose an integration.`,
    noIntegration: `None`,
    integrationSearchPlaceholder: `Search integrations...`,
    integrationSearchAriaLabel: `Search SMS relay integrations by name`,
    noIntegrationMatches: `No integration matches that search.`,
    campaignIdLabel: `Campaign ID`,
    campaignIdHelp: `Required for sending SMS. Prior to setting this value, ensure that a campaign ID is linked to this number in the respective provider. Failure to do so may cause disruption of service, delivery failures, and/or fines.`,
    titleWithNumber: `Edit Phone Number — {{number}}`,
    advancedSection: `Advanced`,
    e911NotConfigured: `Not configured`,
    extensionSearchPlaceholder: `Search extensions...`,
    extensionSearchAriaLabel: `Search extensions by name or number`,
    noExtensionMatches: `No extension matches that search.`,
    cancel: `Cancel`,
    save: `Save`,
  },
  create: {
    title: `Create Phone Number`,
    numberLabel: `Phone Number`,
    numberPlaceholder: `+1234567890`,
    numberHelp: `Must include country code (e.g. +18015551234 for US, +442079460958 for UK).`,
    numberHelpWithPrefix: `Enter the number without the country code.`,
    selectType: `— Select Type —`,
    saveButton: `Create`,
    createMore: `Create more`,
    searchStateProvince: `Search...`,
    noStateProvinceMatches: `No matches`,
  },
  validation: {
    numberRequired: `Phone number is required`,
    numberInvalid: `Enter a valid phone number (e.g. +1 (801) 555-1234)`,
    numberInvalidNational: `Enter a valid phone number (e.g. (801) 555-1234)`,
    numberMissingCountryCode: `Phone number must include a country code (e.g. +1 for US, +44 for UK)`,
    nameRequired: `Name is required`,
    typeRequired: `Type is required`,
    statusRequired: `Status is required`,
    voiceProviderRequired: `Voice provider is required`,
    callerIdTooLong: `Max 15 characters`,
    callerIdInvalidChars: `Only letters, numbers, and spaces allowed`,
    callerIdAllDigits: `Cannot be all digits`,
    campaignIdFormat: `Must be exactly 7 alphanumeric characters`,
    sourceTrackingFormat: `1-6 alphanumeric characters only`,
    e911Incomplete: `Address, City, State, and Postal Code are all required when any E911 field is filled`,
  },
  pagination: {
    resultsPerPage: `Results per page:`,
    previousPage: `Previous page`,
    nextPage: `Next page`,
    jumpToPage: `Jump to page {{page}}`,
    all: `All`,
    recordRange: `{{start}}-{{end}} of {{total}}`,
    recordRangeEmpty: `0 of 0`,
  },
} as const
